import React from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { FONT_SIZES, PALETTE, Z_INDEX } from '~/assets/styles/base';

type ProgressTrackerListProps = {
  $width?: string;
};

const ProgressTrackerList = styled.ul<ProgressTrackerListProps>`
  display: flex;
  margin: 0 auto;
  padding: 20px 0 40px;
  text-align: center;
  width: ${({ $width }) => $width};
`;

const ProgressStep = styled.li`
  flex: 1 1 0%;
  min-width: 24px;
  position: relative;
  &::after {
    content: '';
    background-color: ${PALETTE.primary.main};
    position: absolute;
    z-index: ${Z_INDEX.minus};
    top: 10px;
    right: -50%;
    width: 100%;
    height: 1px;
    transition: background-color 0.3s, background-position 0.3s;
  }
  &:last-child {
    flex-grow: 1;
    ::after {
      display: none;
    }
  }
`;

type ProgressMarkerProps = {
  $status: string;
};

const ProgressMarker = styled.div<ProgressMarkerProps>`
  position: relative;
  margin-bottom: 8px;
  &::before {
    background-color: ${({ $status }) =>
      $status === 'done' ? PALETTE.primary.main : PALETTE.white};
    border-width: ${({ $status }) => ($status === 'doing' ? '6px' : '2px')};
    border-color: ${PALETTE.primary.main};
    border-style: solid;
    content: '';
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: ${Z_INDEX.base};
    width: 21px;
    height: 21px;
    border-radius: 50%;
    transition: background-color, border-color;
    transition-duration: 0.3s;
    margin-left: auto;
    margin-right: auto;
  }
`;

const StyledTypography = styled(Typography)`
  font-size: ${FONT_SIZES.body3};
`;

const getProgressStatus = (i: number, currentNum: number) => {
  if (i < currentNum) {
    return 'done';
  }
  if (i === currentNum) {
    return 'doing';
  }
  return 'toDo';
};

type Props = {
  width: string;
  labels: Array<string>;
  currentNum: number;
};

const ProgressTracker: React.FC<Props> = ({ width, labels, currentNum }): JSX.Element => (
  <ProgressTrackerList $width={width} data-testid="uiParts-ProgressTrackerList">
    {labels.map((label, i) => {
      const status = getProgressStatus(i, currentNum);
      return (
        <ProgressStep key={label} data-testid={`uiParts-ProgressStep-${i}`}>
          <ProgressMarker $status={status} data-testid={`uiParts-ProgressMaker-${status}`} />
          <StyledTypography>{label}</StyledTypography>
        </ProgressStep>
      );
    })}
  </ProgressTrackerList>
);

export default ProgressTracker;
